import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const STATS = [
  { v: 10, suffix: "", l: "Disciplines, one roof" },
  { v: 1, suffix: "", l: "Integrated team, zero handoffs" },
  { v: 24, suffix: "h", l: "To your first response" },
  { v: 100, suffix: "%", l: "AI-native delivery" },
];

const Counter = ({ stat, i, inView }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setVal(stat.v);
      return;
    }
    const duration = 1800 + i * 120;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(eased * stat.v);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, stat.v, i]);

  const display = Math.round(val).toString();

  return (
    <div data-testid={`stat-${i}`} className="flex flex-col gap-3">
      <span className="text-[10px] tracking-[0.4em] uppercase text-muted-ink">P/{String(i + 1).padStart(2, "0")}</span>
      <span
        className="font-display font-medium text-[#0a0a0b] tracking-tight leading-none"
        style={{ fontSize: "clamp(2.6rem, 6vw, 5rem)" }}
      >
        {display}
        <span className="accent-text">{stat.suffix}</span>
      </span>
      <span className="text-sm md:text-base text-muted-ink">{stat.l}</span>
    </div>
  );
};

const Proof = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      ref={ref}
      data-testid="proof-section"
      id="proof"
      className="relative px-6 md:px-12 lg:px-20 py-24 md:py-32"
    >
      <div className="flex items-end justify-between mb-12 md:mb-16 flex-wrap gap-6">
        <div className="flex items-center gap-4">
          <span className="h-px w-12 bg-[#0a0a0b]/30" />
          <span className="text-[10px] tracking-[0.4em] uppercase text-muted-ink">05 — Proof</span>
        </div>
        <h2
          data-testid="proof-headline"
          className="font-display font-medium tracking-tight leading-none text-[#0a0a0b] max-w-3xl"
          style={{ fontSize: "clamp(1.8rem, 3.6vw, 3.4rem)" }}
        >
          How we&apos;re <span className="accent-text">built.</span>
        </h2>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12">
        {STATS.map((s, i) => (
          <Counter key={i} stat={s} i={i} inView={inView} />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="mt-20 md:mt-24 max-w-4xl"
      >
        <p
          data-testid="proof-trust-line"
          className="font-display font-medium tracking-tight text-[#0a0a0b] leading-[1.1]"
          style={{ fontSize: "clamp(1.6rem, 3.2vw, 3rem)" }}
        >
          We don&apos;t pitch decks. <span className="accent-text">We ship outcomes.</span>
        </p>
      </motion.div>
    </section>
  );
};

export default Proof;
